/**
 * THE WALK — docs/hierarchical-region-system.md §3.2.
 *
 * For a chunk request: take the roots that overlap it, and descend only as far as the LOD earns.
 *
 *   if !overlaps(node.aabb, window)   -> prune          <- the entire efficiency story
 *   if lod <= spec.maxLod             -> spec.emit(node)
 *   if lod <= spec.expandAtLod        -> walk each child
 *
 * A node may both draw itself AND expand: that is what lets a city block carry its road grid while
 * its buildings are separate children. A node that does neither at this level costs one AABB test.
 *
 * DEPTH-FIRST IN CHILD ORDER, and the order is part of the contract rather than an implementation
 * detail — nodes write into the same voxels, so last writer wins. A kind that needs its children
 * composited in a particular order (buildings after trees, so their interior carve removes the
 * foliage) returns them in that order and the walk preserves it.
 */
import { aabbOverlaps } from './NodeTypes.js';
export class NodeResolver {
    specs = new Map();
    register(spec) {
        this.specs.set(spec.kind, spec);
    }
    spec(kind) {
        return this.specs.get(kind);
    }
    /** Every kind registered, so a guard can hold ALL of them to a rule — this one's included. */
    kinds() {
        return [...this.specs.keys()];
    }
    /** Walk `roots` against a chunk, emitting whatever the level reaches. */
    resolve(roots, sink, ctx) {
        for (const root of roots)
            this.walk(root, sink, ctx);
    }
    walk(node, sink, ctx) {
        if (!aabbOverlaps(node.aabb, sink.window))
            return;
        const spec = this.specs.get(node.kind);
        if (!spec)
            return;
        if (sink.lod <= spec.maxLod)
            spec.emit(node, sink, ctx);
        if (sink.lod > spec.expandAtLod)
            return;
        for (const child of spec.children(node, ctx, sink))
            this.walk(child, sink, ctx);
    }
    /**
     * The fine-voxel Y span everything under `roots` occupies over a chunk COLUMN — the analytic
     * counterpart of `resolve`, for the vertical clip.
     *
     * Expansion is bounded the same way the draw is, so this cannot be more expensive than drawing —
     * and no kind's `ySpan` may expand its own children to answer, or the clip would cost what the
     * whole subtree costs and `generateLodColumn` runs BEFORE any chunk in the column is generated.
     */
    span(roots, sink, ctx) {
        let bottom = Infinity, top = -Infinity;
        const visit = (node) => {
            if (!aabbOverlaps(node.aabb, sink.window))
                return;
            const spec = this.specs.get(node.kind);
            if (!spec)
                return;
            if (sink.lod <= spec.maxLod) {
                const s = spec.ySpan(node, ctx, sink);
                if (s) {
                    if (s.bottom < bottom)
                        bottom = s.bottom;
                    if (s.top > top)
                        top = s.top;
                }
            }
            if (sink.lod > spec.expandAtLod)
                return;
            for (const child of spec.children(node, ctx, sink))
                visit(child);
        };
        for (const root of roots)
            visit(root);
        return isFinite(top) ? { bottom, top } : null;
    }
}
//# sourceMappingURL=NodeResolver.js.map